import { AddressData } from "@/common/types/address";
import MaskedTextField from "../MaskedTextField";
import { unFormatCnpj as unFormatMask } from "@/common/utils/unFormatters";
import TextField from "../TextField";
import { FieldErrors } from "react-hook-form";
import { AddressValidationSchema } from "@/common/schemas/address.schema";
import useAddressForm from "@/common/hooks/useAddressForm";
import AddressMapImage from "../AddressMapImage";

export interface AddressFormProps {
  initialAddress: AddressData;
  onSetAddress?: (address: AddressData, valid: boolean) => void;
  disableFields?: boolean;
  errors: FieldErrors<AddressValidationSchema>;
  markFieldsAsRequired?: boolean;
}

const AddressForm = ({
  initialAddress,
  onSetAddress,
  disableFields,
  errors,
  markFieldsAsRequired,
}: AddressFormProps) => {
  const {
    address,
    setZipcodeField,
    setAddressField,
    isSearchZipcodePending
  } = useAddressForm({
    initialAddress, onSetAddress,
  });

  return (
    <div className="flex gap-6">
      <div className="w-1/2 flex flex-col gap-4">
        <MaskedTextField
          mask="00000-000"
          label="CEP"
          value={address?.zipcode || initialAddress?.zipcode || ""}
          errorMessage={errors?.zipcode?.message || undefined}
          onBlur={(e) => setZipcodeField(unFormatMask((e.target as HTMLInputElement).value))}
          disabled={disableFields}
          RightIcon={isSearchZipcodePending ? <div className="w-4 h-4 border-4 border-dashed rounded-full animate-spin border-blue-800 mb-1 mr-1" /> : <></>}
          markAsRequired={markFieldsAsRequired}
          name="zipcode"
        />

        <div className="flex gap-2">
          <TextField
            label="Logradouro"
            className="w-3/4"
            defaultValue={address?.street || initialAddress?.street || ""}
            value={address?.street || ""}
            onChange={(e) => setAddressField("street", (e.target as HTMLInputElement).value)}
            errorMessage={errors?.street?.message || undefined}
            disabled={disableFields}
            markAsRequired={markFieldsAsRequired}
            name="street"
          />

          <MaskedTextField
            label="Número"
            name="number"
            mask="00000"
            className="w-1/4"
            defaultValue={address?.number || initialAddress?.number || ""}
            value={address?.number || ""}
            onChange={(e) => setAddressField("number", (e.target as HTMLInputElement).value)}
            errorMessage={errors?.number?.message || undefined}
            disabled={disableFields}
            markAsRequired={markFieldsAsRequired}
          />
        </div>

        <TextField
          label="Município"
          defaultValue={address?.city?.display_name || initialAddress?.city?.display_name || ""}
          value={address?.city?.display_name || ""}
          errorMessage={errors?.city_id?.message || undefined}
          disabled
          markAsRequired={markFieldsAsRequired}
          name="city"
        />

        <TextField
          label="Bairro"
          maxLength={64}
          value={address?.neighborhood || ""}
          defaultValue={address?.neighborhood || initialAddress?.neighborhood || ""}
          errorMessage={errors?.neighborhood?.message || undefined}
          onChange={(e) => setAddressField("neighborhood", (e.target as HTMLInputElement).value)}
          disabled={disableFields}
          markAsRequired={markFieldsAsRequired}
          name="neighborhood"
        />

        <TextField
          label="Estado"
          defaultValue={address?.city?.state?.display_name || initialAddress?.city?.state?.display_name || ''}
          value={address?.city?.state?.display_name || ''}
          disabled
          markAsRequired={markFieldsAsRequired}
          name="state"
        />

        <TextField
          label="Complemento"
          defaultValue={address?.complement || initialAddress?.complement || ""}
          value={address?.complement || ""}
          onChange={(e) => setAddressField("complement", (e.target as HTMLInputElement).value)}
          disabled={disableFields}
          name="complement"
        />
      </div>

      <AddressMapImage address={initialAddress || address } />
    </div>
  );
};

export default AddressForm;
